import type { ParcelPropertiesInfo } from "../types";

// Every field travels as text, exactly as `parseParcelPropertiesInfo` produces it today. Four of them are
// numbers in the tiles, but stringifying here keeps the row identical to the one the old main-thread path
// produced, which is what makes the two comparable field by field.
const PARCEL_COLUMNS = [
	"id",
	"numer",
	"gmina",
	"obreb",
	"pole_geometryczne",
	"pole_ewidencyjne",
	"grupa_rejestrowa",
	"klasouzytki_egib",
	"data_publikacji",
	"mpzp_przeznaczenie",
	"pog_pum",
	"mpzp_pum",
] as const satisfies ReadonlyArray<keyof ParcelPropertiesInfo>;

type ParcelColumn = (typeof PARCEL_COLUMNS)[number];

/** One column of strings: UTF-8 bytes plus `count + 1` byte offsets, both transferable. */
type ParcelStringColumn = {
	bytes: Uint8Array;
	offsets: Uint32Array;
};

type ParcelColumns = Record<ParcelColumn, ParcelStringColumn>;

// UTF-8 never needs more than 3 bytes per UTF-16 code unit for the BMP, and a surrogate pair is 2 units
// for 4 bytes, so 3x the string length is a safe upper bound for `encodeInto`.
const MAX_BYTES_PER_CODE_UNIT = 3;

function encodeParcelStringColumn(values: ReadonlyArray<string>): ParcelStringColumn {
	const encoder = new TextEncoder();
	const offsets = new Uint32Array(values.length + 1);

	let bytes = new Uint8Array(Math.max(64, values.length * 16));
	let written = 0;

	for (let index = 0; index < values.length; index++) {
		offsets[index] = written;

		const value = values[index];
		const required = written + value.length * MAX_BYTES_PER_CODE_UNIT;

		if (required > bytes.length) {
			const grown = new Uint8Array(Math.max(required, bytes.length * 2));
			grown.set(bytes.subarray(0, written));
			bytes = grown;
		}

		written += encoder.encodeInto(value, bytes.subarray(written)).written;
	}

	offsets[values.length] = written;

	// `slice` rather than `subarray`: each column must own its buffer, or transferring one would take the
	// others with it.
	return { bytes: bytes.slice(0, written), offsets };
}

function decodeParcelString(column: ParcelStringColumn, index: number, decoder: TextDecoder): string {
	return decoder.decode(column.bytes.subarray(column.offsets[index], column.offsets[index + 1]));
}

/** Every buffer in the payload, for the transfer list of `postMessage`. */
function collectParcelColumnBuffers(columns: ParcelColumns): Array<ArrayBufferLike> {
	return PARCEL_COLUMNS.flatMap((column) => [columns[column].bytes.buffer, columns[column].offsets.buffer]);
}

export {
	collectParcelColumnBuffers,
	decodeParcelString,
	encodeParcelStringColumn,
	PARCEL_COLUMNS,
	type ParcelColumn,
	type ParcelColumns,
	type ParcelStringColumn,
};
